<template>
    <div class="map-mobile-trajectory">
        <map-mobile-trajectory-header
            v-if="!isDesktopDevice"
            :start-placeholder="!startPointComputed"
            :end-placeholder="!endPoint"
            :start-highlighted="startFromVirtualCurrentLocation"
            @select-start="openSelect('start')"
            @select-end="openSelect('end')">
            <template #start>
                <template v-if="startFromVirtualCurrentLocation">
                    <div class="d-flex justify-content-between">
                        {{ $t('map.myCurrentPosition') }}
                        <div v-if="startFromCurrentLocationLoading">
                            <i class="fas fa-spinner-third fa-spin fa-2x f-12" />
                        </div>
                    </div>
                </template>
                <template v-else-if="startPointComputed">
                    {{ startPointName }}
                </template>
                <template v-else>
                    {{ $t('map.trajectory.selectStartPoint') }}
                </template>
            </template>
            <template #end>
                <template v-if="endPoint">
                    {{ endPoint.getFullName() }}
                </template>
                <template v-else>
                    {{ $t('map.trajectory.chooseDestination') }}
                </template>
            </template>
            <template v-if="canSwapEndpoints" #actions>
                <b-btn variant="light" class="ratio-1x1" pill :disabled="!canSwapEndpoints" @click="invertNavPoints">
                    <i :class="[fontAwesomePrefix, 'fa-arrow-down-arrow-up']" />
                </b-btn>
            </template>
        </map-mobile-trajectory-header>

        <e-drawer ref="trajectoryDrawer" :snap-points="mapIntegrations.isAccessibilityNavigationEnabled() ? ['170px'] : ['135px']"
            :cover-height="300" :sidebar="isDesktopDevice" show-previous-button :label="isMobileDevice ? statsLabel : ''"
            :sticky-header="isDesktopDevice" :no-share-button="!canStart || isDesktopDevice" :title="drawerTitle" :no-content-padding="isDesktopDevice"
            top-mobile-margin="120px" @close="$emit('close')" @share="$emit('share-link')" expand-on-header-click
            header-class="trajectory-sheet-header">

            <template #above>
                <div v-if="previewImage" class="image-preview" @click="imageModal = true">
                    <b-img :src="previewImage" fluid />
                    <div class="destination-preview-badge">
                        <div class="destination-preview-name text-ellipsis">{{ $t(`map.destination`) }}</div>
                    </div>
                </div>
                <div class="d-flex flex-column ml-auto mt-auto">
                    <b-btn variant="white" @click="$emit('reset-camera')" class="pill-btn-xl">
                        <i :class="[fontAwesomePrefix, 'fa-crosshairs']" />
                    </b-btn>
                </div>
            </template>

            <template v-if="previewImage && !isDesktopDevice" #cover-image>
                <div class="cover-image" @click="imageModal = true">
                    <b-img :src="previewImage" fluid />
                </div>
            </template>

            <template v-if="isDesktopDevice" #header>
                <map-mobile-trajectory-header
                    desktop
                    :start-placeholder="!startPointComputed"
                    :end-placeholder="!endPoint"
                    :start-highlighted="startFromVirtualCurrentLocation"
                    @select-start="openSelect('start')"
                    @select-end="openSelect('end')">
                    <template #start>
                        <template v-if="startFromVirtualCurrentLocation">
                            {{ $t('map.myCurrentPosition') }}
                        </template>
                        <template v-else-if="startPointComputed">
                            {{ startPointName }}
                        </template>
                        <template v-else>
                            {{ $t('map.trajectory.selectStartPoint') }}
                        </template>
                    </template>
                    <template #end>
                        <template v-if="endPoint">
                            {{ endPoint.getFullName() }}
                        </template>
                        <template v-else>
                            {{ $t('map.trajectory.chooseDestination') }}
                        </template>
                    </template>
                    <template #actions>
                        <b-dropdown size="md" variant="light" class="ratio-1x1" no-caret>
                            <template #button-content>
                                <i :class="[fontAwesomePrefix, 'fa-ellipsis']" />
                            </template>
                            <b-dropdown-item @click="$emit('share-link')">
                                <i class="fa-share ml-1" :class="fontAwesomePrefix" />
                                {{ $t('map.assistant.sendToPhone') }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <b-btn variant="light" class="ratio-1x1" pill :disabled="!canSwapEndpoints" @click="invertNavPoints">
                            <i :class="[fontAwesomePrefix, 'fa-arrow-down-arrow-up']" />
                        </b-btn>
                    </template>
                </map-mobile-trajectory-header>

                <div class="d-flex align-items-center mb-3">
                    <div class="mr-auto">
                        <div class="text-muted text-left">{{ $t('map.assistant.pathTo') }}</div>
                        <div v-if="isDesktopDevice" class="drawer-title" style="padding: 0">
                            {{ endPointName }}
                        </div>
                        <div class="stats">
                            <div class="stat">
                                <div class="stat-value">{{ durationText || '—' }}</div>
                                <div class="stat-label">min</div>
                            </div>
                            <div class="stat">
                                <div class="stat-value">{{ distanceText || '—' }}</div>
                                <div class="stat-label">{{ $t('map.assistant.distance') }}</div>
                            </div>
                        </div>
                    </div>
                    <div v-if="isDesktopDevice" @click="imageModal = true" style="max-width: 100px;"
                        class="ml-2 cursor-pointer hover-scale">
                        <b-img :src="previewImage" fluid rounded class="border rounded-lg" />
                    </div>
                </div>
            </template>

            <div>
                <div v-if="isDesktopDevice && canStart" class="d-flex w-100 px-3 py-2">
                    <div>
                        <e-qr-code class="qr-code" :data="`${baseSharingUrl}&medium=sharing&source=user`" :size="130"
                            :margins="0" :image="cmsWebsite.design.favicon.src" />
                    </div>
                    <div class="d-flex flex-column justify-content-center text-left">
                        <div class="text-left f-11">
                            <span>{{ $t('map.assistant.qrScanInstruction') }}</span>
                        </div>
                        <b-btn class="mt-1 w-100" size="sm" variant="accent" @click="$emit('share-link')">
                            <span>{{ $t('map.assistant.sendToPhone') }}</span>
                            <i class="fa-share ml-1" :class="fontAwesomePrefix" />
                        </b-btn>
                    </div>
                </div>

                <div v-if="!isDesktopDevice" class="drawer-footer">
                    <div v-if="mapIntegrations.isAccessibilityNavigationEnabled()" class="mode-toggle">
                        <b-btn class="mode-btn" variant="text" @click="setAccessibleMode(false)"
                            :class="{ 'selected-mode': !navSettings.preferences.accessible }">
                            <i :class="[fontAwesomePrefix, 'fa-person-walking']" class="mr-1" />
                            {{ $t('map.trajectory.fastest') }}
                        </b-btn>
                        <b-btn class="mode-btn" variant="text" @click="setAccessibleMode(true)"
                            :class="{ 'selected-mode': navSettings.preferences.accessible }">
                            <i :class="[fontAwesomePrefix, 'fa-wheelchair']" class="mr-1" />
                            {{ $t('map.trajectory.accessible') }}
                        </b-btn>
                    </div>

                    <div class="d-flex justify-content-center align-items-center w-100 mt-1">
                        <e-toast :title="$t('map.trajectory.routeIncompleteTitle')" :message="$t('map.trajectory.routeIncompleteMessage')" position="top-center" offset-y="10px"
                            as-wrapper variant="warning" icon="fa-triangle-exclamation" full-width
                        >
                            <template #default="{ open }">
                                <b-btn variant="accent" size="lg" class="start-btn w-100" @click="onStartButtonClick(open)" >
                                    <i :class="[fontAwesomePrefix, startButtonIcon]" />
                                    {{ startButtonLabel }}
                                </b-btn>
                            </template>
                        </e-toast>

                        <b-btn variant="light" size="lg" class="start-btn ml-2" @click="toggleInstructions">
                            <i :class="[fontAwesomePrefix, `fa-list`]"/>
                        </b-btn>
                    </div>
                </div>

                <map-items-selector
                        v-if="endPoint && endPoint.cmsItem && endPoint.cmsItem.mapItems && endPoint.cmsItem.mapItems.length > 1"
                        :map-items="endPoint.cmsItem.mapItems"
                        :selected-map-item-id="endPoint.id"
                        @selected="selectEndPointMapItem" class="mt-3"/>

                <div v-if="navPath && navPath.instructions && navPath.instructions.length" class="nav-container"
                    :class="{ 'nav-container-desktop': isDesktopDevice }">
                    <div v-for="(instruction, instructionIndex) in navPath.instructions" :key="instructionIndex" class="navigation-item">
                        <div class="nav-icon">
                            <i :class="['fas', instruction.icon || 'fa-location-arrow']" />
                        </div>
                        <div class="nav-text">
                            <map-instruction :map-instruction="instruction" no-line-clamp/>
                        </div>
                    </div>
                </div>
                <template v-else>
                    <map-mobile-location-info v-if="endPoint?.cmsItem" :map-item="endPoint"/>

                    <div v-else class="nav-container nav-empty-state" :class="{ 'nav-container-desktop': isDesktopDevice }">
                        <div class="empty-state-icon">
                            <i :class="[fontAwesomePrefix, 'fa-route']" />
                        </div>
                        <div class="empty-state-message no-instruction">
                            {{ $t('map.trajectory.routeIncompleteMessage') }}
                        </div>
                    </div>
                </template>

                <div class="px-lg-2 w-100" v-if="cmsWebsite.settings.testing?.enabled">
                    <b-btn variant="light" class="py-2" block @click="$store.commit(`showFeedbackModal`, {action: `trajectory`, startPoint, endPoint})">
                        <i :class="[fontAwesomePrefix, 'fa-comment-smile']" />
                        <span class="ml-1" v-tr>Give Feedback|Donner des commentaires</span>
                    </b-btn>
                </div>
            </div>

            <template #footer>
                <div v-if="isDesktopDevice" class="drawer-footer p-3 pt-1">
                    <div v-if="mapIntegrations.isAccessibilityNavigationEnabled()" class="mode-toggle mb-2">
                        <b-btn class="mode-btn" variant="text" @click="setAccessibleMode(false)"
                            :class="{ 'selected-mode': !navSettings.preferences.accessible }">
                            <i :class="[fontAwesomePrefix, 'fa-person-walking']" class="mr-1" />
                            {{ $t('map.trajectory.fastest') }}
                        </b-btn>
                        <b-btn class="mode-btn" variant="text" @click="setAccessibleMode(true)"
                            :class="{ 'selected-mode': navSettings.preferences.accessible }">
                            <i :class="[fontAwesomePrefix, 'fa-wheelchair']" class="mr-1" />
                            {{ $t('map.trajectory.accessible') }}
                        </b-btn>
                    </div>

                    <e-toast :title="$t('map.trajectory.routeIncompleteTitle')" :message="$t('map.trajectory.routeIncompleteMessage')"
                        as-wrapper variant="warning" icon="fa-triangle-exclamation"
                        position="top-center" offset-y="8px"
                    >
                        <template #default="{ open }">
                            <b-btn @click="onStartButtonClick(open)" variant="accent" size="lg" class="start-btn w-100 mt-1"
                                style="width: 400px;"
                            >
                                <i :class="[fontAwesomePrefix, startButtonIcon]" />
                                {{ startButtonLabel }}
                            </b-btn>
                        </template>
                    </e-toast>
                </div>
            </template>
        </e-drawer>

        <map-mobile-search-modal
            :visible.sync="selectModal.visible" :target-type="selectModal.type" v-on="$listeners"
            :latest-start-point="latestStartPoint"
            :latest-destination-point="latestDestinationPoint"
            :start-from-current-location="startFromCurrentLocation"
            :start-from-current-location-loading="startFromCurrentLocationLoading"
            :start-from-current-location-disabled="startFromCurrentLocationDisabled"
            @open-qr-scanner="$emit('open-qr-scanner')"/>

        <e-mobile-modal v-model="imageModal" position="center" :title="endPointName">
            <div class="w-100 ratio-1x1 rounded-lg bg-light overflow-hidden">
                <b-img :src="previewImage" :alt="endPointName" block fluid />
            </div>
        </e-mobile-modal>
    </div>
</template>

<script>
import { formatDistance as helperFormatDistance } from '@/helpers/DistanceHelper.js';
import EDrawer from "@/components/e-drawer.vue";
import EQrCode from "@vue-components/components/e-qr-code.vue";
import ESearchInput from "@vue-components/components/e-search-input.vue";
import EMobileModal from "@/components/e-mobile-modal.vue";
import EToast from "@/components/e-toast.vue";
import MapMixin from "@/helpers/maps/map-mixin.js";
import CmsAnalyticsSearch from "../../../../common/db/models/cms/CmsAnalyticsSearch.js";
import SessionTracker from "@/helpers/SessionTracker.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import MapMobileSearchModal from "@components/maps/mobile/map-mobile-search-modal.vue";
import MapMobileTrajectoryHeader from "@/components/maps/mobile/map-mobile-trajectory-header.vue";
import MapInstruction from "@components/maps/mobile/map-instruction.vue";
import MapMobileLocationInfo from "@components/maps/mobile/map-mobile-location-info.vue";
import MapItemsSelector from "@components/maps/mobile/map-items-selector.vue";
import EActionSheetItem from "@vue-components/components/e-action-sheet-item.vue";

export default {
    name: `map-mobile-trajectory-overlay`,
    components: {
        EActionSheetItem,
        MapItemsSelector, MapMobileLocationInfo, MapInstruction, MapMobileTrajectoryHeader, MapMobileSearchModal, ESearchInput, EDrawer, EQrCode, EMobileModal, EToast },
    mixins: [MapMixin],
    props: {
        map: { type: Object },
        currentLocation: { type: Object },
        gpsCurrentLocation: { type: Object },
        startPoint: { type: Object },
        endPoint: { type: Object },
        navPath: { type: Object },
        latestStartPoint: { type: Object, default: null },
        latestDestinationPoint: { type: Object, default: null },
        startFromCurrentLocation: { type: Boolean, default: false },
        startFromQrCode: { type: Boolean, default: false },
        startFromCurrentLocationLoading: { type: Boolean, default: false },
        startFromCurrentLocationDisabled: { type: Boolean, default: false }
    },
    data() {
        return {
            selectModal: {
                visible: false,
                type: `start`,
            },
            imageModal: false,
            search: ``,
            analyticsSearch: null
        };
    },
    computed: {
        startPointComputed() {
            return this.startPoint;
        },
        startPointName() {
            return this.startPoint.getFullName() || this.tr(this.startPoint.description) || this.tr(this.startPoint.location) || this.$t(`map.trajectory.lastStartPosition`);
        },
        startFromVirtualCurrentLocation() {
            return this.startFromCurrentLocation || this.startFromQrCode;
        },
        drawerTitle() {
            if (this.isDesktopDevice) {
                return this.$t(`map.trajectory.title`);
            }
            if (this.endPoint) {
                return this.endPointName;
            }
            if (this.startFromVirtualCurrentLocation) {
                return this.$t(`map.myCurrentPosition`);
            }
            return this.$t(`map.trajectory.title`);
        },
        endPointName() {
            return this.endPoint ? this.endPoint.getFullName() : this.$t(`map.trajectory.title`);
        },
        previewImage() {
            if (this.navPath?.previewImage) {
                return this.navPath.previewImage;
            } else if (this.endPoint?.image) {
                return this.endPoint.image;
            } else if (this.endPoint?.cmsItem?.getPreviewImage) {
                return this.endPoint.cmsItem.getPreviewImage();
            }
            return null;
        },
        secondaryValue() {
            return this.endPoint?.cmsItem?.getSecondaryValue ? this.$tr(this.endPoint.cmsItem.getSecondaryValue()) : null;
        },
        statsLabel() {
            if (this.navPath?.distance == null) {
                return null;
            }
            const distance = this.navPath?.distance == null ? null : helperFormatDistance(this.navPath.distance, { style: `spaced` });
            const duration = this.navPath?.duration == null ? null : this.durationText;
            return distance && duration ? `${duration} min l ${distance}` : distance || duration || null;
        },
        durationText() {
            if (this.navPath?.duration == null) {
                return null;
            }
            return `${this.navPath.duration}`;
        },
        distanceText() {
            if (this.navPath?.distance == null) {
                return null;
            }
            return helperFormatDistance(this.navPath.distance, { style: `spaced` });
        },
        canStart() {
            return !!(this.navPath && this.endPoint && this.startPoint);
        },
        missingTargetType() {
            if (!this.startPointComputed && !this.startFromVirtualCurrentLocation) {
                return `start`;
            }
            if (!this.endPoint) {
                return `end`;
            }
            return null;
        },
        startButtonLabel() {
            if (this.canStart) {
                return this.$t(`map.start`);
            }
            if (this.missingTargetType === `start`) {
                return this.$t(`map.trajectory.selectStartPoint`);
            }
            if (this.missingTargetType === `end`) {
                return this.$t(`map.trajectory.chooseDestination`);
            }
            return this.$t(`map.start`);
        },
        startButtonIcon() {
            return this.canStart ? `fa-location-arrow` : `fa-location-dot`;
        },
        canSwapEndpoints() {
            return !this.startFromVirtualCurrentLocation;
        },
        baseSharingUrl() {
            let baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
            const startQuery = this.$t(`map.assistant.queryStart`);
            const endQuery = this.$t(`map.assistant.queryEnd`);
            return `${baseUrl}?${startQuery}=${this.navPath.start.getPermalink()}&${endQuery}=${this.endPoint.getPermalink()}`;
        }
    },
    // this removes one step when coming from home screen, but makes it less clear for the user
    // mounted() {
    //     if (this.endPoint && !this.startPoint && !this.isKioskDevice) {
    //         this.openSelect(`start`);
    //     }
    // },
    methods: {
        setAccessibleMode(accessible) {
            this.navSettings.preferences.accessible = accessible;
            this.$emit(`nav-preferences-changed`, this.navSettings.preferences);
        },
        openSelect(type) {
            this.selectModal.type = type;
            this.selectModal.visible = true;
        },
        invertNavPoints() {
            this.$emit(`swap-endpoints`);
        },
        selectEndPointMapItem(mapItem) {
            this.$emit(`endpoint-selected`, {
                target: `end`,
                type: `map-item`,
                mapItemId: mapItem.id
            });
            this.$refs.trajectoryDrawer?.snapToMin();
        },
        onStartButtonClick(openToast) {
            if (!this.canStart) {
                if (this.missingTargetType) {
                    this.openSelect(this.missingTargetType);
                    return;
                }
                if (openToast) {
                    openToast();
                }
                return;
            }

            this.$emit(`start-navigation`);
        },
        toggleInstructions() {
            const drawer = this.$refs.trajectoryDrawer;
            if (!drawer) return;
            drawer.sheetPosition > 200 ? drawer.snapToMin() : drawer.snapToMax();
        }
    },
};
</script>

<style lang="scss" scoped>
@import "./map-mobile-navigation-shared.scss";

:deep(.e-drawer.sidebar-drawer .trajectory-sheet-header) {
    background: var(--sheet-secondary-background);
    border-bottom: 1px solid var(--border-color);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.start-btn {
    border-radius: 8px;
    white-space: nowrap;
}

.stats {
    display: flex;
    gap: 12px;
    margin-left: 4px;

    .stat {
        display: flex;
        align-items: baseline;
        gap: 4px;
        text-align: left;

        .stat-value {
            font-size: 11pt;
            font-weight: 700;
            color: var(--color-dark);
        }

        .stat-label {
            font-size: 11pt;
            color: var(--color-muted);
        }
    }
}

.mode-toggle {
    display: flex;
    gap: 8px;
    background: var(--color-light);
    border-radius: 8px;
    padding: 4px;

    .mode-btn {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-weight: 500;
    }
}

.selected-mode {
    background: hsla(var(--color-primary-h), var(--color-primary-s), var(--color-primary-l), 0.15);
    border: 1px solid hsla(var(--color-primary-h), var(--color-primary-s), var(--color-primary-l), 0.25) !important;
    border-radius: 4px;
}

.image-preview {
    position: relative;
    width: 110px;
    height: 110px;
    border-radius: 14px;
    border: 2px solid #fff;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
    padding: 0;
    pointer-events: auto;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.destination-preview-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 1px 8px;

    background: rgba(255, 255, 255, 0.95);
}

.destination-preview-name {
    font-size: 9.5pt;
    font-weight: 700;
    color: var(--color-dark);
}

.cancel-button-container {
    position: fixed;
    bottom: 0;
    left: auto;
    right: auto;
    padding: 16px;
    display: flex;
    justify-content: center;

    .cancel-btn {
        min-width: 200px;
    }
}

.nav-container {
    --map-mobile-nav-item-padding: 20px 12px 20px 16px;
    --map-mobile-nav-instruction-lines: 3;
    transition: height 200ms ease-in-out;
    background: white;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding: 0;
    overflow-y: auto;

    &.nav-container-desktop {
        .nav-text .instruction {
            line-clamp: unset;
            -webkit-line-clamp: unset;
        }

        .navigation-item {
            height: auto;
        }
    }

    .navigation-item+.navigation-item {
        border-top: var(--border-width) solid var(--border-color);
    }

    &.nav-empty-state {
        align-items: center;
        text-align: center;
        padding: 18px 14px;
        margin-top: 16px;
        border-radius: 14px;
        border: 1px dashed var(--border-color);
        // background: linear-gradient(180deg, #ffffff, var(--color-light));
        gap: 10px;

        .empty-state-icon {
            width: 46px;
            height: 46px;
            border-radius: 50%;
            display: grid;
            place-items: center;
            font-size: 18px;
            color: var(--color-accent);
            background: rgba(34, 122, 241, 0.12);
        }

        .empty-state-title {
            font-size: 12pt;
            font-weight: 700;
            color: var(--color-dark);
            line-height: 1.2;
        }

        .empty-state-message {
            font-size: 10.5pt;
            color: var(--color-muted);
            max-width: 32ch;
            line-height: 1.35;
        }

        &.nav-container-desktop {
            min-height: 180px;
            justify-content: center;
            padding: 24px;
        }
    }
}
</style>







